import { z } from 'zod';
import { LocationType } from '../../constants/location-type.js';
import { REQUIRED_LOCATION_TYPE_LABELS } from '../../constants/required-location-types.js';
import { ADDRESS_CONSTRAINTS } from '../address/constraints.js';
import { locationDescriptionSchema, locationNameSchema } from '../location/update-form.js';
import { optionalFormString, optionalFormTrimmedString, requiredEnumIdString, } from '../zod-helpers/index.js';
import { addAddressIssues } from './address-form-rules.js';
import { MAX_LOCATION_GROUP_LOCATIONS, MAX_LOCATION_GROUP_LOCATIONS_MESSAGE, } from './constraints.js';
import { locationGroupNameSchema } from './create-form.js';
import { checkDuplicateLocationNames, checkRequiredLocationTypes, } from './rules.js';
const locationRowSchema = z.object({
    // WHY: name is checked in addLocationNameIssues instead, which re-paths the shared schema's
    // message onto locations.N.name; description has no such indexing need.
    name: z.string(),
    description: locationDescriptionSchema,
    typeId: requiredEnumIdString(LocationType, 'Location type is required.', 'Invalid location type selection.'),
    activeFlag: z.boolean(),
    pickable: z.boolean(),
    receivable: z.boolean(),
    countedAsAvailable: z.boolean(),
    /** Marks the two seeded rows whose type and status are locked and which cannot be removed. */
    isRequired: z.boolean(),
});
/**
 * Name format is the form's own concern -- the API input trusts the schema that already parsed it,
 * so only the rows whose name is well-formed reach the shared duplicate rule below.
 */
function addLocationNameFormatIssues(locations, context) {
    const rows = [];
    locations.forEach((location, index) => {
        const nameResult = locationNameSchema.safeParse(location.name);
        if (!nameResult.success) {
            context.addIssue({
                code: 'custom',
                message: nameResult.error.issues[0].message,
                path: ['locations', index, 'name'],
            });
            return;
        }
        rows.push({
            name: nameResult.data,
            // The form holds typeId as a string, since a select's value always is one.
            typeId: Number(location.typeId),
            activeFlag: location.activeFlag,
            index,
        });
    });
    return rows;
}
export const locationGroupCreateFormValuesSchema = z
    .object({
    name: locationGroupNameSchema,
    activeFlag: z.boolean(),
    // WHY: bounded here as well as server-side -- an over-long paste would otherwise pass the
    // form and fail only on the round trip.
    address: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.address.maxLength),
    city: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.city.maxLength),
    stateId: optionalFormString(),
    countryId: optionalFormString(),
    zip: optionalFormTrimmedString(ADDRESS_CONSTRAINTS.zip.maxLength),
    locations: z
        .array(locationRowSchema)
        .max(MAX_LOCATION_GROUP_LOCATIONS, MAX_LOCATION_GROUP_LOCATIONS_MESSAGE),
})
    .superRefine((values, context) => {
    addAddressIssues(values, context);
    const rows = addLocationNameFormatIssues(values.locations, context);
    for (const issue of [
        ...checkDuplicateLocationNames(rows),
        ...checkRequiredLocationTypes(rows),
    ]) {
        context.addIssue({ code: 'custom', message: issue.message, path: issue.path });
    }
});
/** WHY: built from REQUIRED_LOCATION_TYPE_LABELS so a type added there is seeded and named too. */
function seededRow([typeId, label]) {
    return {
        name: label,
        description: '',
        typeId: String(typeId),
        activeFlag: true,
        pickable: true,
        receivable: true,
        countedAsAvailable: true,
        isRequired: true,
    };
}
export const EMPTY_LOCATION_GROUP_CREATE_FORM = {
    name: '',
    activeFlag: true,
    address: '',
    city: '',
    stateId: '',
    countryId: '',
    zip: '',
    locations: [...REQUIRED_LOCATION_TYPE_LABELS].map(seededRow),
};
//# sourceMappingURL=create-form-values.js.map